import type { ConstValues } from '@openx/types/helpers';
import type { JsonValue } from 'type-fest';

export const KnownMessageEventType = {
  FUNCTION_CALL: 'function_call',
  FUNCTION_RESPONSE: 'function_response',
} as const;

export type KnownMessageEventType = ConstValues<typeof KnownMessageEventType>;

export type MessageEventType = KnownMessageEventType | (string & {});

export type MessageEventContent = {
  type?: MessageEventType;
  name?: string;
  args?: JsonValue;
  response?: JsonValue;
};

export type MessageEvent = {
  role: typeof MessageRole.EVENT;
  author: string;
  timestamp: string;
  invocation_id: string;
  content?: MessageEventContent | null;
};

/**
 * An answer widget from session history, one per message. ox-api-core emits it beside the agent
 * message of the same `invocation_id`; the answer text places it with an `openx-widget` marker
 * naming `widget.id`.
 */
export type MessageWidget = {
  role: typeof MessageRole.WIDGET;
  author: string;
  timestamp: string;
  invocation_id: string;
  widget?: { id?: unknown; provider?: unknown; payload?: unknown } | null;
};

export type ThinkingEvent = {
  role: typeof MessageRole.THINKING;
  messages: string[];
  timestamp: string;
};

export type MessageUserOrAgent = {
  text: string;
  timestamp: string;
  invocation_id: string;
  summary?: string | null;
};

export type MatchResultValue = string | number;

export type AudienceMatch = {
  id: string;
  name: string;
  score: number;
  reach: MatchResultValue;
};

export type AudienceResultsStats = {
  relevance: MatchResultValue;
  viewability: MatchResultValue;
  cpm: MatchResultValue;
  reach: MatchResultValue;
};

export type InventoryMatch = {
  id: string;
  name: string;
  score: number;
  marketRequests: MatchResultValue;
};

export type InventoryResultsStats = {
  relevance: MatchResultValue;
  avgCpm: MatchResultValue;
  viewability: MatchResultValue;
  bidRate: MatchResultValue;
};

export const RichContentActionType = {
  SHOW_MATCH_DETAILS: 'show_match_details',
  CREATE_DEAL: 'create_deal',
} as const;

export type RichContentActionType = ConstValues<typeof RichContentActionType>;

export type RichContentAction = {
  id: string;
  type: RichContentActionType;
  label: string;
  isConfirmed?: boolean;
};

export const RichContentType = {
  AUDIENCE_RESULTS: 'audience_results',
  INVENTORY_RESULTS: 'inventory_results',
} as const;

export type AudienceResultsContent = {
  /** Stable identifier of the content item, unique within its message. */
  id: string;
  type: typeof RichContentType.AUDIENCE_RESULTS;
  matches: AudienceMatch[];
  stats: AudienceResultsStats;
  actions?: RichContentAction[];
};

export type InventoryResultsContent = {
  /** Stable identifier of the content item, unique within its message. */
  id: string;
  type: typeof RichContentType.INVENTORY_RESULTS;
  matches: InventoryMatch[];
  stats: InventoryResultsStats;
  actions?: RichContentAction[];
};

export type RichContent = AudienceResultsContent | InventoryResultsContent;

export const MessageRole = {
  WELCOMING: 'agent_welcome',
  ALERT: 'alert',
  USER: 'user',
  AGENT: 'agent',
  EVENT: 'event',
  WIDGET: 'widget',
  THINKING: 'thinking',
} as const;

export type AgentMessage = { role: typeof MessageRole.AGENT; richContent?: RichContent[] | null } & MessageUserOrAgent;

export type Message =
  | ({ role: typeof MessageRole.USER } & MessageUserOrAgent)
  | AgentMessage
  | MessageEvent
  | MessageWidget
  | ({ role: typeof MessageRole.WELCOMING } & MessageUserOrAgent)
  | ({ role: typeof MessageRole.ALERT } & MessageUserOrAgent);

export type OpenXAgentMessage = Message | ThinkingEvent;

export type SuggestionOption = {
  label: string;
  message: string;
  /** When provided and conversation is empty, suggestion is shown as a detailed card with this description. */
  description?: string;
  Icon: React.ElementType;
};
